import React, { useState } from 'react'
import { Button, Input } from 'antd'
import { SendOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { VeeSendOutcome } from './hooks'

interface IVeeComposerProps {
  disabled: boolean
  // The draft is mirrored to VeeAssistant via onChange so it survives the panel
  // unmounting (e.g. yielding to Crisp); local state keeps keystrokes from
  // re-rendering the whole panel.
  initialValue: string
  onChange: (value: string) => void
  onSend: (message: string) => Promise<VeeSendOutcome>
  placeholderId?: string
}

export const VeeComposer: React.FC<IVeeComposerProps> = ({ disabled, initialValue, onChange, onSend, placeholderId = 'veeAssistant.placeholder' }) => {
  const { formatMessage } = useIntl()
  const [value, setValue] = useState(initialValue)
  const [sending, setSending] = useState(false)

  const change = (next: string) => {
    setValue(next)
    onChange(next)
  }

  const submit = async () => {
    const message = value.trim()
    if (!message || disabled || sending) {
      return
    }
    setSending(true)
    const outcome = await onSend(message)
    setSending(false)
    // A failed or limit-blocked message stays in the box so the user doesn't retype it.
    if (outcome === 'accepted') {
      change('')
    }
  }

  return (
    <div className="vee-composer">
      <Input.TextArea
        autoFocus
        value={value}
        onChange={(event) => change(event.target.value)}
        onPressEnter={(event) => {
          if (!event.shiftKey) {
            event.preventDefault()
            void submit()
          }
        }}
        onKeyDown={(event) => {
          // Escape with a draft blurs instead of letting the panel's document handler close Vee.
          if (event.key === 'Escape' && value.trim()) {
            event.stopPropagation()
            event.currentTarget.blur()
          }
        }}
        placeholder={formatMessage({ id: placeholderId })}
        autoSize={{ minRows: 1, maxRows: 5 }}
        maxLength={4000}
      />
      <Button type="primary" icon={<SendOutlined />} onClick={() => void submit()} disabled={disabled || sending || !value.trim()} aria-label={formatMessage({ id: 'app.send' })} />
    </div>
  )
}
